import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { gql } from '../../graphql/client';
import { M_DELETE_ASSET, Q_LIST_ALL_ASSETS } from '../../graphql/operations';
import type { Asset } from '../../types/forge';
import { uploadAsset } from './uploadAsset';

/**
 * Asset library — org-scoped CMS for images / icons / splash screens.
 *
 * The full library lives at /assets so authors can browse and curate
 * regardless of which app is open. The same data is reachable in-line
 * via the AssetPicker modal from App Settings (icon/splash) and (later)
 * the View Builder image source field.
 */
export function AssetsScreen() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadKind, setUploadKind] = useState<'image' | 'icon' | 'splash'>('image');
  const [uploadError, setUploadError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['assets', 'all'],
    queryFn: () => gql<{ Asset: Asset[] }>(Q_LIST_ALL_ASSETS),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => uploadAsset({ file, kind: uploadKind }),
    onSuccess() {
      setUploadError(null);
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
    onError(err: Error) {
      setUploadError(err.message);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => gql(M_DELETE_ASSET, { id }),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
  });

  const items = list.data?.Asset ?? [];

  return (
    <div style={s.shell}>
      <header style={s.header}>
        <Link to="/" style={s.back}>← Apps</Link>
        <h1 style={s.title}>Assets</h1>
      </header>

      <main style={s.main}>
        <section style={s.uploadCard}>
          <h2 style={s.cardTitle}>Upload an asset</h2>
          <p style={s.cardHint}>
            Pick a kind so it shows up in the right picker. PNG / JPG / WebP
            up to 10 MB.
          </p>
          <div style={s.uploadRow}>
            <select
              value={uploadKind}
              onChange={(e) => setUploadKind(e.target.value as typeof uploadKind)}
              style={s.select}
            >
              <option value="image">image</option>
              <option value="icon">icon</option>
              <option value="splash">splash</option>
            </select>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload.mutate(f);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            />
            <button
              type="button"
              style={s.uploadBtn}
              disabled={upload.isPending}
              onClick={() => fileInputRef.current?.click()}
            >
              {upload.isPending ? 'Uploading…' : '+ Upload'}
            </button>
            {uploadError && <span style={s.error}>{uploadError}</span>}
          </div>
        </section>

        <section>
          {list.isLoading && <p style={s.empty}>Loading…</p>}
          {list.error && (
            <p style={s.error}>
              Failed to load: {(list.error as Error).message}
            </p>
          )}
          {!list.isLoading && items.length === 0 && (
            <p style={s.empty}>No assets yet. Upload one above to get started.</p>
          )}
          {items.length > 0 && (
            <ul style={s.grid}>
              {items.map((a) => (
                <li key={a.id} style={s.card}>
                  <img
                    src={a.storage_url}
                    alt={a.name}
                    style={s.thumb}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.opacity = '0.2';
                    }}
                  />
                  <div style={s.meta}>
                    <span style={s.metaName} title={a.name}>{a.name}</span>
                    <span style={s.metaKind}>
                      {a.kind} · {formatSize(a.file_size ?? null)}
                    </span>
                    <a
                      href={a.storage_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      style={s.metaUrl}
                    >
                      Copy URL
                    </a>
                  </div>
                  <button
                    type="button"
                    style={s.cardDelete}
                    title="Delete"
                    onClick={() => {
                      if (window.confirm(`Delete "${a.name}"?`)) {
                        remove.mutate(a.id);
                      }
                    }}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}

function formatSize(n: number | null): string {
  if (n == null) return '?';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

const s: Record<string, React.CSSProperties> = {
  shell: { minHeight: '100vh', backgroundColor: '#F8FAFC', fontFamily: 'system-ui, sans-serif' },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '12px 24px',
    borderBottom: '1px solid #E2E8F0',
    backgroundColor: '#fff',
  },
  back: { fontSize: 13, color: '#4F46E5', textDecoration: 'none' },
  title: { margin: 0, fontSize: 18, fontWeight: 700, color: '#0F172A' },
  main: { maxWidth: 960, margin: '32px auto', padding: '0 24px' },
  uploadCard: {
    backgroundColor: '#fff',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    padding: 20,
    marginBottom: 24,
  },
  cardTitle: { margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#0F172A' },
  cardHint: { margin: '0 0 16px', fontSize: 12, color: '#94A3B8' },
  uploadRow: { display: 'flex', alignItems: 'center', gap: 12 },
  select: {
    padding: '8px 10px',
    fontSize: 13,
    border: '1px solid #CBD5E1',
    borderRadius: 4,
  },
  uploadBtn: {
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#4F46E5',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
  grid: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: 16,
  },
  card: {
    position: 'relative',
    backgroundColor: '#fff',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    overflow: 'hidden',
  },
  thumb: {
    width: '100%',
    height: 140,
    objectFit: 'cover',
    backgroundColor: '#F8FAFC',
    display: 'block',
  },
  meta: {
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  metaName: {
    fontSize: 12,
    fontWeight: 600,
    color: '#0F172A',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  metaKind: { fontSize: 10, color: '#94A3B8', textTransform: 'uppercase' as const, letterSpacing: 0.4 },
  metaUrl: { marginTop: 6, fontSize: 11, color: '#4F46E5', textDecoration: 'none' },
  cardDelete: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 24,
    height: 24,
    fontSize: 14,
    border: 'none',
    backgroundColor: 'rgba(15,23,42,0.6)',
    color: '#fff',
    borderRadius: '50%',
    cursor: 'pointer',
    lineHeight: 1,
  },
  empty: { fontSize: 13, color: '#94A3B8', textAlign: 'center' as const, padding: 24 },
  error: { fontSize: 12, color: '#c00' },
};
